import React, { useMemo } from 'react';
import {
  BadgeLabelList,
  MAX_VISIBLE_BADGES_MOBILE,
} from '@/components/orders/badge-label-list';
import { BORDER, FONTS, shadow, TYPOGRAPHY } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import {
  DISPLAY_SIZE_TO_IMAGE_SIZE,
  OrderableItemListItemImage,
} from '@/components/orders/orderable-item-list-item-image';
import { EqualHeightTouchableOpacity } from '@/components/equal-height-touchable-opacity';
import { ItemListOption } from '@/contexts/item-context-types';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { OrderableItemListItemActions } from '@/components/orders/orderable-item-list-item-actions';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useItemQuantityState } from '@/stores/new-order-quantity-store';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  item: ItemListOption;
  onSelect: () => void;
  updateQuantity: UpdateQuantityFn;
}

const ADS_CAROUSEL_IMAGE_SIZE =
  DISPLAY_SIZE_TO_IMAGE_SIZE[ListDisplaySize.Small];
const LIST_ITEM_WIDTH = 298;

export const AdvertisedListItem: React.FC<Props> = ({
  containerStyle,
  item,
  onSelect,
  updateQuantity,
}) => {
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const [styles] = useStyles();
  const metadata = item.metadata!;

  const skus = metadata.skus;
  const rowVariantPackUUIDs = useMemo(
    () => skus.map((sku) => sku.variantPackUUID),
    [skus],
  );
  const quantityState = useItemQuantityState(rowVariantPackUUIDs);
  const altText = [metadata.displayName, metadata.externalID]
    .filter(Boolean)
    .join('-');

  return (
    <EqualHeightTouchableOpacity
      focusable={false}
      onPress={onSelect}
      style={[styles.base, containerStyle]}
    >
      <View style={styles.container}>
        <View style={styles.productImageAndDescription}>
          <View style={styles.productImageContainer}>
            <OrderableItemListItemImage
              alt={altText}
              hidePlaceholderImages={hidePlaceholderImages}
              imageURLs={metadata.photoURLs}
              listDisplaySize={ListDisplaySize.Small}
            />
          </View>
          <View style={styles.productTitleAndDescription}>
            <Text
              numberOfLines={1}
              style={[
                TYPOGRAPHY.medium,
                FONTS.primarySemiBold,
                styles.productTitle,
              ]}
            >
              {item.title}
            </Text>
            {metadata.description ? (
              <Text
                numberOfLines={1}
                style={[TYPOGRAPHY.description, styles.productDescription]}
              >
                {metadata.description}
              </Text>
            ) : null}
            <BadgeLabelList
              badges={metadata?.badges}
              iconOnly
              maxVisible={MAX_VISIBLE_BADGES_MOBILE}
            />
          </View>
        </View>
        <View style={BORDER.primary}>
          <OrderableItemListItemActions
            isFocused={false}
            onPress={onSelect}
            quantityState={quantityState}
            showListView
            skus={skus}
            updateQuantity={updateQuantity}
          />
        </View>
      </View>
    </EqualHeightTouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    borderRadius: 8,
    marginRight: 8,
    ...shadow({
      color: theme.colors.black,
      elevation: 2,
      opacity: 0.1,
      radius: 12,
      x: 0,
      y: 0,
    }),
  },
  container: {
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    flex: 1,
    padding: 16,
    width: LIST_ITEM_WIDTH,
  },
  productDescription: {
    lineHeight: 16,
  },
  productImageAndDescription: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  productImageContainer: {
    ...BORDER.primary,
    alignItems: 'flex-end',
    height: ADS_CAROUSEL_IMAGE_SIZE,
    marginRight: 16,
    overflow: 'hidden',
    width: ADS_CAROUSEL_IMAGE_SIZE,
  },
  productTitle: {
    marginBottom: 4,
  },
  productTitleAndDescription: {
    flex: 1,
  },
}));
